import React, { useEffect, useRef } from 'react';
import './InfiniteScrollLoader.css';

/**
 * Bottom sentinel for infinite scroll. Fires onLoadMore once per viewport entry;
 * re-arms when the sentinel leaves so scrolling can load further batches.
 */
const InfiniteScrollLoader = ({
  hasMore,
  onLoadMore,
  loading = false,
  loadingLabel = 'Loading more products…',
  rootMargin = '400px 0px',
  className = 'infinite-scroll-loader',
  resetKey,
}) => {
  const ref = useRef(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  const armedRef = useRef(true);

  useEffect(() => {
    armedRef.current = true;
  }, [resetKey]);

  useEffect(() => {
    if (!hasMore) return undefined;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;

    armedRef.current = true;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries.find(Boolean);
        if (!entry) return;
        if (!entry.isIntersecting) {
          armedRef.current = true;
          return;
        }
        if (!armedRef.current || loading) return;
        armedRef.current = false;
        onLoadMoreRef.current();
      },
      { rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loading, rootMargin, resetKey]);

  if (!hasMore) return null;

  return (
    <div className={className} ref={ref} aria-live="polite">
      {loading ? (
        <>
          <span className="infinite-scroll-loader__spinner" aria-hidden="true" />
          <span>{loadingLabel}</span>
        </>
      ) : null}
    </div>
  );
};

export default InfiniteScrollLoader;
